import { Sparkles, Gem, Scissors, Brush, Flower2, Wand2, Eye, LucideIcon } from 'lucide-react'
import { SERVICE_IMAGES } from '../data'
import { Lang, Localized } from './types'

interface RawServiceItem {
  name: Localized
  desc: Localized
  price: Localized
  note?: Localized
  image?: string
}

interface RawServiceCategory {
  icon: LucideIcon
  name: Localized
  image?: string
  imageAlt?: Localized
  items: RawServiceItem[]
}

export interface ServiceItem {
  name: string
  desc: string
  price: string
  note?: string
  image?: string
}

export interface ServiceCategory {
  icon: LucideIcon
  name: string
  image?: string
  imageAlt?: string
  items: ServiceItem[]
}

const byLength: Localized = { es: 'Precio según largo', en: 'Price based on length' }
const customQuote: Localized = { es: 'Cotización personalizada', en: 'Custom quote' }
const consult: Localized = { es: 'Consulta', en: 'Inquire' }

const from = (amount: string): Localized => ({ es: `Desde ${amount}`, en: `From ${amount}` })

const rawCategories: RawServiceCategory[] = [
  {
    icon: Sparkles,
    name: { es: 'Coloración & Técnicas de Color', en: 'Color & Coloring Techniques' },
    image: SERVICE_IMAGES.balayage,
    imageAlt: {
      es: 'Balayage con transición dorada natural y ondas suaves',
      en: 'Balayage with a natural golden transition and soft waves',
    },
    items: [
      {
        name: { es: 'Retoque de Raíz', en: 'Root Touch-Up' },
        desc: {
          es: 'Renovación del color desde la raíz para mantener tu tono uniforme y radiante. Incluye consulta de colorimetría personalizada.',
          en: 'Color refresh at the roots to keep your tone even and radiant. Includes a personalized color consultation.',
        },
        price: from('$60'),
        image: SERVICE_IMAGES.retoqueRaiz,
      },
      {
        name: { es: 'Coloración Completa', en: 'Full Color' },
        desc: {
          es: 'Cambio total de color con tonos a medida para tu piel, estilo de vida y personalidad. Resultado profesional y duradero.',
          en: 'A complete color change with tones tailored to your skin, lifestyle and personality. Professional, long-lasting results.',
        },
        price: from('$140'),
        image: SERVICE_IMAGES.colorCompleto2,
      },
      {
        name: { es: 'Balayage', en: 'Balayage' },
        desc: {
          es: 'La técnica de iluminación artística a mano que da ese efecto natural de "beso de sol". Suave, elegante y de bajo mantenimiento.',
          en: 'The hand-painted lightening technique that creates that natural sun-kissed effect. Soft, elegant and low maintenance.',
        },
        price: from('$280'),
        image: SERVICE_IMAGES.balayage,
      },
      {
        name: { es: 'Highlights / Mechas', en: 'Highlights' },
        desc: {
          es: 'Iluminaciones estratégicas que aportan dimensión, movimiento y luminosidad al cabello. Perfectas para cualquier tono base.',
          en: 'Strategic lightening that adds dimension, movement and luminosity. Perfect for any base tone.',
        },
        price: from('$280'),
        image: SERVICE_IMAGES.highlights,
      },
      {
        name: { es: 'Money Piece Full Face', en: 'Money Piece Full Face' },
        desc: {
          es: 'Iluminación enmarcando el rostro para aportar luz inmediata a tu mirada. El cambio más rápido y favorecedor sin tocar todo el cabello.',
          en: 'Face-framing lightening that brings instant brightness to your look. The fastest, most flattering change without touching all your hair.',
        },
        price: from('$120'),
        image: SERVICE_IMAGES.moneyPieceFullFace,
      },
    ],
  },
  {
    icon: Gem,
    name: { es: 'Tratamientos Capilares Premium', en: 'Premium Hair Treatments' },
    image: SERVICE_IMAGES.keratina,
    imageAlt: {
      es: 'Cabello con acabado liso y brillo espejo tras tratamiento de keratina',
      en: 'Sleek hair with mirror shine after a keratin treatment',
    },
    items: [
      {
        name: { es: 'Keratina', en: 'Keratin' },
        desc: {
          es: 'Alisado progresivo que elimina el frizz, sella la fibra capilar y aporta brillo intenso. Ideal para cabello grueso y rebelde.',
          en: 'Progressive smoothing that removes frizz, seals the hair fiber and adds intense shine. Ideal for thick, unruly hair.',
        },
        price: from('$200'),
        note: byLength,
        image: SERVICE_IMAGES.keratina,
      },
      {
        name: { es: 'Alisado Láser', en: 'Laser Straightening' },
        desc: {
          es: 'Tecnología láser que sella el tratamiento hebra por hebra para un liso espejo, duradero y con máximo brillo.',
          en: 'Laser technology that seals the treatment strand by strand for a long-lasting, mirror-sleek finish with maximum shine.',
        },
        price: from('$400'),
        note: byLength,
        image: SERVICE_IMAGES.alisadoGinger,
      },
      {
        name: { es: 'Alisado Japonés', en: 'Japanese Straightening' },
        desc: {
          es: 'Alisado de alta duración que reestructura el cabello para un resultado completamente liso y sedoso.',
          en: 'Long-lasting straightening that restructures the hair for a completely sleek, silky result.',
        },
        price: from('$250'),
        note: byLength,
        image: SERVICE_IMAGES.alisadoJapones,
      },
      {
        name: { es: 'Bótox Capilar', en: 'Hair Botox' },
        desc: {
          es: 'Reconstrucción profunda que rellena, hidrata y revitaliza cada hebra desde adentro. Sin químicos agresivos.',
          en: 'Deep reconstruction that fills, hydrates and revitalizes every strand from within. No harsh chemicals.',
        },
        price: from('$180'),
        note: byLength,
        image: SERVICE_IMAGES.botoxCapilar,
      },
      {
        name: { es: 'Nanoplastia', en: 'Nanoplasty' },
        desc: {
          es: 'La evolución del alisado. Nanopartículas que alisan, nutren y restauran el cabello sin formaldehído.',
          en: 'The evolution of straightening. Nanoparticles that smooth, nourish and restore hair without formaldehyde.',
        },
        price: from('$80'),
        note: byLength,
        image: SERVICE_IMAGES.nanoplastia,
      },
      {
        name: { es: 'Kera-Botox', en: 'Kera-Botox' },
        desc: {
          es: 'Lo mejor de dos mundos: el alisado de la keratina con la nutrición reconstructora del bótox capilar.',
          en: 'The best of both worlds: keratin smoothing with the rebuilding nourishment of hair botox.',
        },
        price: from('$180'),
        note: byLength,
        image: SERVICE_IMAGES.keraBotox,
      },
      {
        name: { es: 'Keraplastia', en: 'Keraplasty' },
        desc: {
          es: 'Tratamiento de keratina reforzada que reduce el volumen, controla el frizz y deja un acabado pulido.',
          en: 'Reinforced keratin treatment that reduces volume, controls frizz and leaves a polished finish.',
        },
        price: from('$150'),
        note: byLength,
        image: SERVICE_IMAGES.keraplastia,
      },
      {
        name: { es: 'Lifting Capilar', en: 'Hair Lifting' },
        desc: {
          es: 'Reestructuración que devuelve cuerpo, movimiento y elasticidad al cabello debilitado.',
          en: 'Restructuring that brings body, movement and elasticity back to weakened hair.',
        },
        price: from('$100'),
        image: SERVICE_IMAGES.liftingCapilar,
      },
      {
        name: { es: 'Cocktail Capilar', en: 'Hair Cocktail' },
        desc: {
          es: 'Mezcla de activos personalizada según el diagnóstico de tu cabello. Hidratación exprés con resultado inmediato.',
          en: 'A custom blend of actives based on your hair diagnosis. Express hydration with immediate results.',
        },
        price: from('$50'),
        image: SERVICE_IMAGES.cocktailCapilar,
      },
      {
        name: { es: 'Repolarización Antifrizz', en: 'Anti-Frizz Repolarization' },
        desc: {
          es: 'Sella la cutícula y devuelve el equilibrio al cabello poroso. Adiós al frizz, hola brillo.',
          en: 'Seals the cuticle and restores balance to porous hair. Goodbye frizz, hello shine.',
        },
        price: from('$80'),
        image: SERVICE_IMAGES.repolarizacion,
      },
      {
        name: { es: 'Tratamiento Bambú', en: 'Bamboo Treatment' },
        desc: {
          es: 'Fortalecimiento natural con extracto de bambú que aporta resistencia, suavidad y luminosidad.',
          en: 'Natural strengthening with bamboo extract that adds resilience, softness and shine.',
        },
        price: from('$80'),
        image: SERVICE_IMAGES.tratamientoBambu,
      },
      {
        name: { es: 'Cirugía Capilar', en: 'Hair Surgery' },
        desc: {
          es: 'Reparación intensiva para cabello muy dañado por químicos o calor. Reconstruye la fibra desde el interior.',
          en: 'Intensive repair for hair badly damaged by chemicals or heat. Rebuilds the fiber from the inside out.',
        },
        price: from('$180'),
        note: byLength,
        image: SERVICE_IMAGES.cirugiaCapilar,
      },
      {
        name: { es: 'Spa Capilar', en: 'Hair Spa' },
        desc: {
          es: 'Ritual completo de limpieza profunda, masaje capilar y nutrición. Relajación total para ti y tu cabello.',
          en: 'A complete ritual of deep cleansing, scalp massage and nourishment. Total relaxation for you and your hair.',
        },
        price: from('$100'),
        image: SERVICE_IMAGES.spaCapilar,
      },
      {
        name: { es: 'Deep Conditioning Treatment', en: 'Deep Conditioning Treatment' },
        desc: {
          es: 'Acondicionamiento profundo que restaura la hidratación y deja el cabello suave, manejable y con movimiento.',
          en: 'Deep conditioning that restores hydration and leaves hair soft, manageable and full of movement.',
        },
        price: from('$40'),
        image: SERVICE_IMAGES.deepConditioning,
      },
    ],
  },
  {
    icon: Scissors,
    name: { es: 'Cortes & Styling', en: 'Cuts & Styling' },
    image: SERVICE_IMAGES.corteDama,
    imageAlt: {
      es: 'Corte para dama con acabado pulido y brillo natural',
      en: "Women's haircut with a polished finish and natural shine",
    },
    items: [
      {
        name: { es: 'Corte para Dama', en: "Women's Haircut" },
        desc: {
          es: 'Corte personalizado según la forma de tu rostro, textura del cabello y estilo de vida. Incluye lavado y secado.',
          en: 'A cut tailored to your face shape, hair texture and lifestyle. Includes wash and blow-dry.',
        },
        price: from('$35'),
        image: SERVICE_IMAGES.corteDama,
      },
      {
        name: { es: 'Blowout', en: 'Blowout' },
        desc: {
          es: 'Secado con técnica profesional que deja el cabello liso, con volumen o con las ondas perfectas. Dura hasta 3 días.',
          en: 'Professional blow-dry that leaves hair sleek, voluminous or with perfect waves. Lasts up to 3 days.',
        },
        price: { es: '$45', en: '$45' },
        image: SERVICE_IMAGES.blowout,
      },
      {
        name: { es: 'Peinados', en: 'Hairstyling' },
        desc: {
          es: 'Updos elegantes, ondas hollywoodense, trenzas de alta costura y más. Para bodas, quinceañeras y ocasiones especiales.',
          en: 'Elegant updos, Hollywood waves, couture braids and more. For weddings, quinceañeras and special occasions.',
        },
        price: from('$70'),
        note: { es: 'Cotización según evento', en: 'Quote based on event' },
        image: SERVICE_IMAGES.peinado,
      },
    ],
  },
  {
    icon: Brush,
    name: { es: 'Maquillaje Profesional', en: 'Professional Makeup' },
    image: SERVICE_IMAGES.maquillaje,
    imageAlt: {
      es: 'Maquillaje profesional aplicado con brocha, acabado natural y luminoso',
      en: 'Professional makeup applied with a brush, natural luminous finish',
    },
    items: [
      {
        name: { es: 'Maquillaje Social', en: 'Social Makeup' },
        desc: {
          es: 'Look natural y luminoso, perfecto para cenas, reuniones y eventos de día. Larga duración garantizada.',
          en: 'A natural, luminous look, perfect for dinners, gatherings and daytime events. Long wear guaranteed.',
        },
        price: { es: '$60', en: '$60' },
        image: SERVICE_IMAGES.maquillajeSocial,
      },
      {
        name: { es: 'Maquillaje Glow Up', en: 'Glow Up Makeup' },
        desc: {
          es: 'Acabado radiante con piel luminosa y mirada definida. El punto medio ideal entre natural y glamoroso.',
          en: 'A radiant finish with luminous skin and defined eyes. The ideal middle ground between natural and glamorous.',
        },
        price: { es: '$80', en: '$80' },
        image: SERVICE_IMAGES.maquillajeGlowUp,
      },
      {
        name: { es: 'Maquillaje Full Face', en: 'Full Face Makeup' },
        desc: {
          es: 'Maquillaje completo de alta intensidad con acabado impecable para grandes ocasiones y sesiones de fotos.',
          en: 'Full high-impact makeup with a flawless finish for big occasions and photo shoots.',
        },
        price: { es: '$100', en: '$100' },
        image: SERVICE_IMAGES.maquillajeFullFace,
      },
      {
        name: { es: 'Asesoría de Imagen', en: 'Image Consulting' },
        desc: {
          es: 'Analizamos tu paleta de colores, tipo de piel y estilo personal para que luzcas increíble todos los días.',
          en: 'We analyze your color palette, skin type and personal style so you look incredible every day.',
        },
        price: { es: '$60', en: '$60' },
        note: { es: 'Sesión 60 min', en: '60 min session' },
        image: SERVICE_IMAGES.asesoriaImagen,
      },
    ],
  },
  {
    icon: Flower2,
    name: { es: 'Masajes & Bienestar', en: 'Massage & Wellness' },
    image: SERVICE_IMAGES.spaBody,
    imageAlt: {
      es: 'Masaje corporal con piedras calientes en un ambiente de spa con velas',
      en: 'Hot stone body massage in a candlelit spa setting',
    },
    items: [
      {
        name: { es: 'Full Body', en: 'Full Body' },
        desc: {
          es: 'Masaje corporal completo que libera tensión acumulada y devuelve el equilibrio a todo tu cuerpo.',
          en: 'A full body massage that releases built-up tension and restores balance throughout your body.',
        },
        price: { es: '$100', en: '$100' },
        note: { es: '60 min', en: '60 min' },
        image: SERVICE_IMAGES.masajesSpa,
      },
      {
        name: { es: 'Spa Alivio Express', en: 'Express Relief Spa' },
        desc: {
          es: 'Sesión focalizada en cuello, hombros y espalda. El respiro perfecto cuando tienes poco tiempo.',
          en: 'A focused session on neck, shoulders and back. The perfect break when you are short on time.',
        },
        price: { es: '$55', en: '$55' },
        note: { es: '30 min', en: '30 min' },
        image: SERVICE_IMAGES.spaAlivioExpress,
      },
      {
        name: { es: 'Full Experience', en: 'Full Experience' },
        desc: {
          es: 'Masaje completo + reflexología + hidratación. El ritual más completo para desconectar por completo.',
          en: 'Full massage + reflexology + hydration. The most complete ritual to fully disconnect.',
        },
        price: { es: '$140', en: '$140' },
        note: { es: 'Experiencia completa', en: 'Complete experience' },
        image: SERVICE_IMAGES.spaBody,
      },
    ],
  },
  {
    icon: Wand2,
    name: { es: 'Extensiones', en: 'Extensions' },
    image: SERVICE_IMAGES.kTipExtension,
    imageAlt: {
      es: 'Resultado de extensiones de cabello largo con ondas iluminadas',
      en: 'Long hair extension results with illuminated waves',
    },
    items: [
      {
        name: { es: 'Hair Extensions K-Tip', en: 'K-Tip Hair Extensions' },
        desc: {
          es: 'Extensiones de queratina aplicadas hebra por hebra para un resultado natural, ligero y de larga duración.',
          en: 'Keratin extensions applied strand by strand for a natural, lightweight and long-lasting result.',
        },
        price: { es: '$500 – $3,000', en: '$500 – $3,000' },
        note: { es: 'Según cantidad y largo', en: 'Based on quantity and length' },
        image: SERVICE_IMAGES.kTipExtension,
      },
      {
        name: { es: 'Hair Extensions Hand Tied', en: 'Hand Tied Hair Extensions' },
        desc: {
          es: 'Técnica cosida a mano que aporta volumen y largo con máxima comodidad y un acabado invisible.',
          en: 'Hand-sewn technique that adds volume and length with maximum comfort and an invisible finish.',
        },
        price: consult,
        note: customQuote,
        image: SERVICE_IMAGES.extensionHandTied,
      },
      {
        name: { es: 'Hair Extensions Individual', en: 'Individual Hair Extensions' },
        desc: {
          es: 'Colocación individual para densificar zonas específicas o añadir largo de forma discreta.',
          en: 'Individual placement to add density in specific areas or discreetly add length.',
        },
        price: consult,
        note: customQuote,
        image: SERVICE_IMAGES.extensionIndividual,
      },
    ],
  },
  {
    icon: Eye,
    name: { es: 'Facial & Mirada', en: 'Facial & Eyes' },
    image: SERVICE_IMAGES.limpiezaFacial,
    imageAlt: {
      es: 'Limpieza facial profesional en cabina de spa',
      en: 'Professional facial cleansing in a spa room',
    },
    items: [
      {
        name: { es: 'Limpieza Facial', en: 'Facial Cleansing' },
        desc: {
          es: 'Limpieza profunda que purifica, desincrusta e hidrata la piel dejándola fresca y luminosa.',
          en: 'Deep cleansing that purifies, decongests and hydrates the skin, leaving it fresh and luminous.',
        },
        price: from('$80'),
        image: SERVICE_IMAGES.limpiezaFacial,
      },
      {
        name: { es: 'Cejas Perfectas', en: 'Perfect Brows' },
        desc: {
          es: 'Diseño de cejas a medida de tu rostro para enmarcar la mirada con una forma equilibrada y natural.',
          en: 'Brow design tailored to your face to frame your eyes with a balanced, natural shape.',
        },
        price: { es: '$60', en: '$60' },
        image: SERVICE_IMAGES.cejas,
      },
      {
        name: { es: 'Sombreado de Cejas', en: 'Brow Shading' },
        desc: {
          es: 'Relleno y definición con acabado difuminado que aporta densidad y profundidad a tus cejas.',
          en: 'Fill and definition with a soft-blended finish that adds density and depth to your brows.',
        },
        price: { es: '$60', en: '$60' },
        image: SERVICE_IMAGES.sombreadoCejas2,
      },
      {
        name: { es: 'Lifting de Pestañas', en: 'Lash Lift' },
        desc: {
          es: 'Curvatura y elevación de tus pestañas naturales para una mirada abierta y despierta por semanas.',
          en: 'Curl and lift for your natural lashes, for an open, wide-awake look that lasts for weeks.',
        },
        price: { es: '$60', en: '$60' },
        image: SERVICE_IMAGES.pestanas,
      },
    ],
  },
]

export function getServiceCategories(lang: Lang): ServiceCategory[] {
  return rawCategories.map((category) => ({
    icon: category.icon,
    name: category.name[lang],
    image: category.image,
    imageAlt: category.imageAlt ? category.imageAlt[lang] : undefined,
    items: category.items.map((item) => ({
      name: item.name[lang],
      desc: item.desc[lang],
      price: item.price[lang],
      note: item.note ? item.note[lang] : undefined,
      image: item.image,
    })),
  }))
}
